export type WebSocketStatus = 'connecting' | 'open' | 'closed';

export type TopicMessage = Record<string, unknown> & { topic: string };

type TopicListener = (message: TopicMessage) => void;
type StatusListener = (status: WebSocketStatus) => void;

export type WebSocketClientOptions = {
  /** Resolves the full URL to dial, including any auth the server expects.
   * Called before every connection attempt, so single-use credentials work. */
  resolveUrl: () => Promise<string>;
  /** Fallback heartbeat period, used until the server announces its own. */
  heartbeatMs?: number;
  maxReconnectDelayMs?: number;
  onDebug?: (message: string, detail?: unknown) => void;
};

const DEFAULT_HEARTBEAT_MS = 25_000;
const DEFAULT_MAX_RECONNECT_DELAY_MS = 30_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * One reconnecting WebSocket, shared by every topic.
 *
 * Deliberately free of React and of any particular auth scheme: the caller
 * supplies `resolveUrl`, so this works with ticket auth, a token in the query
 * string, or a plain cookie-authenticated URL. Messages are routed by their
 * `topic` field, which is what lets a single connection serve many features
 * instead of one socket per feature.
 */
export default class WebSocketClient {
  private readonly resolveUrl: () => Promise<string>;
  private readonly onDebug?: (message: string, detail?: unknown) => void;
  /** Period configured by the caller, used until a server announces its own. */
  private readonly fallbackHeartbeatMs: number;
  private readonly maxReconnectDelayMs: number;

  private socket: WebSocket | null = null;
  private status: WebSocketStatus = 'closed';
  private stopped = true;
  /** True while `resolveUrl` is in flight. `socket` is still null then, so
   * without this a focus or `online` event would start a second attempt and
   * leave the first one orphaned but still delivering. */
  private connecting = false;
  /** Bumped by `stop()`. An attempt suspended in `resolveUrl` compares this
   * on resume to tell whether it has been superseded — `stop()` alone is not
   * enough to detect, because a later `start()` clears `stopped` again. */
  private generation = 0;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  /** Period currently in force: the fallback until this connection's `hello`
   * overrides it. Reset per connection, so a value announced by a previous
   * server cannot outlive the connection that announced it. */
  private heartbeatMs: number;
  private awaitingPong = false;

  private topicListeners = new Map<string, Set<TopicListener>>();
  private statusListeners = new Set<StatusListener>();

  constructor(options: WebSocketClientOptions) {
    this.resolveUrl = options.resolveUrl;
    this.onDebug = options.onDebug;
    // `??` rather than spreading over defaults: a spread does not skip an
    // explicitly-undefined property, so `heartbeatMs: undefined` would win and
    // leave setInterval with undefined -- a ping every tick, not a heartbeat.
    this.fallbackHeartbeatMs = options.heartbeatMs ?? DEFAULT_HEARTBEAT_MS;
    this.maxReconnectDelayMs =
      options.maxReconnectDelayMs ?? DEFAULT_MAX_RECONNECT_DELAY_MS;
    this.heartbeatMs = this.fallbackHeartbeatMs;
  }

  getStatus() {
    return this.status;
  }

  /** Subscribe to one topic. Returns an unsubscribe function. */
  on(topic: string, listener: TopicListener) {
    const listeners = this.topicListeners.get(topic) ?? new Set();
    listeners.add(listener);
    this.topicListeners.set(topic, listeners);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) this.topicListeners.delete(topic);
    };
  }

  /** Observe connection status. Fires immediately with the current value. */
  onStatusChange(listener: StatusListener) {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => {
      this.statusListeners.delete(listener);
    };
  }

  send(payload: unknown) {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(payload));
    return true;
  }

  start() {
    if (!this.stopped) return;
    this.stopped = false;
    void this.connect();
  }

  stop() {
    this.stopped = true;
    this.generation += 1;
    this.connecting = false;
    this.clearTimers();
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.setStatus('closed');
  }

  /** Reconnect now if the socket is down. Safe to call on focus or `online`. */
  reconnectIfClosed() {
    if (this.stopped) return;
    if (!this.socket || this.socket.readyState === WebSocket.CLOSED) {
      void this.connect();
    }
  }

  private setStatus(status: WebSocketStatus) {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach((listener) => {
      listener(status);
    });
  }

  private clearTimers() {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearHeartbeat();
  }

  private clearHeartbeat() {
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect() {
    if (this.stopped || this.reconnectTimer !== null) return;
    const delay = Math.min(
      1_000 * 2 ** this.reconnectAttempt,
      this.maxReconnectDelayMs,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(
      () => {
        this.reconnectTimer = null;
        void this.connect();
      },
      // Jitter, so a server restart does not bring every client back at once.
      delay + Math.floor(Math.random() * 500),
    );
  }

  private startHeartbeat() {
    this.clearHeartbeat();
    this.awaitingPong = false;
    this.heartbeatTimer = setInterval(() => {
      if (this.socket?.readyState !== WebSocket.OPEN) return;
      if (this.awaitingPong) {
        // The previous ping was never answered, so the connection is dead
        // even though the browser has not noticed. Closing triggers a retry.
        this.onDebug?.('heartbeat missed, reconnecting');
        this.socket.close();
        return;
      }
      this.awaitingPong = true;
      this.send({ type: 'ping' });
    }, this.heartbeatMs);
  }

  private handleMessage(raw: string) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;

    if (parsed.type === 'pong') {
      this.awaitingPong = false;
      return;
    }

    // The server owns the heartbeat period so it can never drift from the
    // liveness timeout it enforces.
    if (parsed.type === 'hello') {
      if (typeof parsed.heartbeat_ms === 'number' && parsed.heartbeat_ms > 0) {
        this.heartbeatMs = parsed.heartbeat_ms;
        this.startHeartbeat();
      }
      return;
    }

    if (typeof parsed.topic !== 'string') return;
    const listeners = this.topicListeners.get(parsed.topic);
    if (!listeners) return;
    const message = parsed as TopicMessage;
    listeners.forEach((listener) => {
      listener(message);
    });
  }

  private async connect() {
    if (
      this.stopped ||
      this.connecting ||
      this.socket?.readyState === WebSocket.OPEN ||
      this.socket?.readyState === WebSocket.CONNECTING
    ) {
      return;
    }

    const generation = this.generation;
    this.connecting = true;
    // Every connection starts from the configured period and adopts whatever
    // *this* server announces, if anything.
    this.heartbeatMs = this.fallbackHeartbeatMs;
    this.setStatus('connecting');
    let url: string;
    try {
      url = await this.resolveUrl();
    } catch (error) {
      if (generation !== this.generation) return;
      this.connecting = false;
      this.onDebug?.('could not resolve socket url', error);
      this.setStatus('closed');
      this.scheduleReconnect();
      return;
    }
    // Superseded while suspended. `stop()` has already reset the shared state
    // and a later `start()` may now own it, so touch nothing — dialling here
    // would orphan the live socket by overwriting `this.socket`.
    if (generation !== this.generation) return;

    const socket = new WebSocket(url);
    this.socket = socket;
    this.connecting = false;

    // Every handler checks it still owns the tracked socket. The in-flight
    // guard above should prevent a second one existing at all, but an orphan
    // that slipped through must stay inert rather than clear the live
    // socket's heartbeat or deliver duplicate messages.
    socket.onopen = () => {
      if (this.socket !== socket) {
        socket.close();
        return;
      }
      this.reconnectAttempt = 0;
      this.setStatus('open');
      this.startHeartbeat();
    };
    socket.onmessage = (event) => {
      if (this.socket !== socket) return;
      if (typeof event.data === 'string') this.handleMessage(event.data);
    };
    socket.onerror = () => socket.close();
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.clearHeartbeat();
      this.setStatus('closed');
      this.scheduleReconnect();
    };
  }
}
